import {
    createContext,
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import { BrowserSpeechEngine } from "./BrowserSpeechEngine.js";
import type {
    SpeechBoundary,
    SpeechEngine,
    SpeechLanguagePreference,
    SpeechLocale,
    SpeechVoiceOption
} from "./SpeechEngine.js";
import {
    normalizeSpeechText,
    resolveSpeechLocale
} from "./speechLanguage.js";
import {
    createSpeechRangeHighlighter,
    createSpeechTextHighlighter,
    findSpeechWord,
    type SpeechTextHighlighter
} from "./speechTextHighlight.js";

type SpeechStatus = "idle" | "speaking" | "paused" | "error" | "unsupported";

interface SpeechSettings {
    language: SpeechLanguagePreference;
    fallbackLocale: SpeechLocale;
    rate: number;
    pitch: number;
    volume: number;
    voiceId: string | null;
}

interface SpeakOptions {
    lang?: SpeechLocale;
    rate?: number;
    pitch?: number;
    volume?: number;
    voiceId?: string;
    segmentBreaks?: readonly number[];
    onBoundary?: (boundary: SpeechBoundary) => void;
    initialWord?: string;
}

interface SpeechContextValue {
    supported: boolean;
    status: SpeechStatus;
    currentText: string | null;
    currentWord: string | null;
    error: string | null;
    settings: SpeechSettings;
    voices: SpeechVoiceOption[];
    updateSettings(patch: Partial<SpeechSettings>): void;
    speak(text: string, options?: SpeakOptions): Promise<void>;
    speakSelection(text: string, range: Range, options?: SpeakOptions): Promise<void>;
    speakElement(element: HTMLElement, options?: SpeakOptions): Promise<void>;
    stop(): void;
    pause(): void;
    resume(): void;
}

interface SpeechProviderProps {
    children: ReactNode;
    /** Enables deterministic tests and a future local/offline implementation. */
    engine?: SpeechEngine;
}

const DEFAULT_SETTINGS: SpeechSettings = {
    language: "auto",
    fallbackLocale: "fr-FR",
    rate: 1,
    pitch: 1,
    volume: 1,
    voiceId: null
};

const SpeechContext = createContext<SpeechContextValue | null>(null);

function clamp(value: number, minimum: number, maximum: number, fallback: number): number {
    return Number.isFinite(value)
        ? Math.min(maximum, Math.max(minimum, value))
        : fallback;
}

function sanitizeSettings(current: SpeechSettings, patch: Partial<SpeechSettings>): SpeechSettings {
    return {
        language: patch.language ?? current.language,
        fallbackLocale: patch.fallbackLocale ?? current.fallbackLocale,
        rate: clamp(patch.rate ?? current.rate, 0.5, 2, current.rate),
        pitch: clamp(patch.pitch ?? current.pitch, 0, 2, current.pitch),
        volume: clamp(patch.volume ?? current.volume, 0, 1, current.volume),
        voiceId: patch.voiceId === undefined ? current.voiceId : patch.voiceId
    };
}

function SpeechProvider({ children, engine: suppliedEngine }: SpeechProviderProps) {
    const engine = useMemo(
        () => suppliedEngine ?? new BrowserSpeechEngine(),
        [suppliedEngine]
    );
    const [supported, setSupported] = useState(false);
    const [status, setStatus] = useState<Exclude<SpeechStatus, "unsupported">>("idle");
    const [currentText, setCurrentText] = useState<string | null>(null);
    const [currentWord, setCurrentWord] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [settings, setSettings] = useState<SpeechSettings>(DEFAULT_SETTINGS);
    const [voices, setVoices] = useState<SpeechVoiceOption[]>([]);
    const jobRef = useRef(0);
    const mountedRef = useRef(true);
    const highlighterRef = useRef<SpeechTextHighlighter | null>(null);

    useEffect(() => {
        mountedRef.current = true;
        const isSupported = engine.isSupported();
        setSupported(isSupported);

        const syncVoices = (): void => {
            setVoices(engine.listVoices?.() ?? []);
        };
        syncVoices();

        const synthesis = typeof window !== "undefined"
            && "speechSynthesis" in window
            ? window.speechSynthesis
            : null;
        synthesis?.addEventListener?.("voiceschanged", syncVoices);

        return () => {
            mountedRef.current = false;
            jobRef.current += 1;
            highlighterRef.current?.clear();
            highlighterRef.current = null;
            synthesis?.removeEventListener?.("voiceschanged", syncVoices);
            engine.stop();
        };
    }, [engine]);

    const updateSettings = useCallback((patch: Partial<SpeechSettings>) => {
        setSettings(current => sanitizeSettings(current, patch));
    }, []);

    const stop = useCallback(() => {
        jobRef.current += 1;
        engine.stop();
        highlighterRef.current?.clear();
        highlighterRef.current = null;
        setStatus("idle");
        setCurrentText(null);
        setCurrentWord(null);
        setError(null);
    }, [engine]);

    const speak = useCallback(async (rawText: string, options: SpeakOptions = {}) => {
        const text = normalizeSpeechText(rawText);
        if (!text) return;

        if (!engine.isSupported()) {
            setSupported(false);
            setStatus("idle");
            setCurrentWord(null);
            setError("La lecture audio n’est pas prise en charge par ce navigateur.");
            return;
        }

        setSupported(true);
        const job = ++jobRef.current;
        const lang = options.lang
            ?? resolveSpeechLocale(text, settings.language, settings.fallbackLocale);
        setCurrentText(text);
        setCurrentWord(options.initialWord ?? null);
        setError(null);
        setStatus("speaking");

        try {
            await engine.speak({
                text,
                lang,
                rate: clamp(options.rate ?? settings.rate, 0.5, 2, settings.rate),
                pitch: clamp(options.pitch ?? settings.pitch, 0, 2, settings.pitch),
                volume: clamp(options.volume ?? settings.volume, 0, 1, settings.volume),
                voiceId: options.voiceId ?? settings.voiceId ?? undefined,
                segmentBreaks: options.segmentBreaks,
                onBoundary: boundary => {
                    if (job !== jobRef.current || !mountedRef.current) return;
                    setCurrentWord(findSpeechWord(text, boundary.charIndex));
                    options.onBoundary?.(boundary);
                }
            });
        } catch (caught) {
            if (job === jobRef.current && mountedRef.current) {
                setStatus("error");
                setCurrentWord(null);
                setError(caught instanceof Error ? caught.message : "La lecture audio a échoué.");
            }
            return;
        }

        if (job === jobRef.current && mountedRef.current) {
            setStatus("idle");
            setCurrentText(null);
            setCurrentWord(null);
        }
    }, [engine, settings]);

    const speakSelection = useCallback(async (
        text: string,
        range: Range,
        options: SpeakOptions = {}
    ) => {
        highlighterRef.current?.clear();
        const highlighter = createSpeechRangeHighlighter(range, text);
        highlighterRef.current = highlighter;
        highlighter.activate(0);

        try {
            await speak(text, {
                ...options,
                initialWord: text,
                onBoundary: boundary => {
                    highlighter.activate(boundary.charIndex);
                    options.onBoundary?.(boundary);
                }
            });
        } finally {
            if (highlighterRef.current === highlighter) {
                highlighter.clear();
                highlighterRef.current = null;
            }
        }
    }, [speak]);

    const speakElement = useCallback(async (
        element: HTMLElement,
        options: SpeakOptions = {}
    ) => {
        highlighterRef.current?.clear();
        const highlighter = createSpeechTextHighlighter(element);
        highlighterRef.current = highlighter;

        try {
            await speak(highlighter.text, {
                ...options,
                segmentBreaks: highlighter.segmentBreaks,
                onBoundary: boundary => {
                    const word = highlighter.activate(boundary.charIndex);
                    if (word && mountedRef.current) setCurrentWord(word);
                    options.onBoundary?.(boundary);
                }
            });
        } finally {
            if (highlighterRef.current === highlighter) {
                highlighter.clear();
                highlighterRef.current = null;
            }
        }
    }, [speak]);


    const pause = useCallback(() => {
        if (status !== "speaking") return;
        engine.pause();
        setStatus("paused");
    }, [engine, status]);

    const resume = useCallback(() => {
        if (status !== "paused") return;
        engine.resume();
        setStatus("speaking");
    }, [engine, status]);

    const effectiveStatus: SpeechStatus = supported ? status : "unsupported";
    const value = useMemo<SpeechContextValue>(() => ({
        supported,
        status: effectiveStatus,
        currentText,
        currentWord,
        error,
        settings,
        voices,
        updateSettings,
        speak,
        speakSelection,
        speakElement,
        stop,
        pause,
        resume
    }), [
        supported,
        effectiveStatus,
        currentText,
        currentWord,
        error,
        settings,
        voices,
        updateSettings,
        speak,
        speakSelection,
        speakElement,
        stop,
        pause,
        resume
    ]);

    return <SpeechContext.Provider value={value}>{children}</SpeechContext.Provider>;
}

function useSpeech(): SpeechContextValue {
    const value = useContext(SpeechContext);
    if (!value) {
        throw new Error("useSpeech must be used inside SpeechProvider.");
    }
    return value;
}

export { SpeechProvider, useSpeech };
export type {
    SpeakOptions,
    SpeechContextValue,
    SpeechProviderProps,
    SpeechSettings,
    SpeechStatus
};
